import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { list as listSupervisors } from '../../../../lib/api/supervisor';
import { list as listJudges } from '../../../../lib/api/judge';
import { listSportsHall } from '../../../../lib/api/club';
import { list as listCompetitions } from '../../../../lib/api/competition';
import { list as listCompetitionClubs } from '../../../../lib/api/competitionClubs';
import {
  editMatch,
  listCompetitionRounds
} from '../../../../lib/api/match';

const getClubEntityId = item => {
  return (
    item?.entities_id ??
    item?.club_entities_id ??
    item?.clubs_entities_id ??
    null
  );
};

const toPersonOption = item => ({
  value: item.entities_id,
  label: `${item.name || ''} ${item.last_name || ''}`.trim() || item.code
});

export function EditMatch({ initialData, onClose, onSave, readOnly = false }) {
  const [competitions, setCompetitions] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [sportsHalls, setSportsHalls] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [judges, setJudges] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedHomeClub, setSelectedHomeClub] = useState(null);
  const [selectedAwayClub, setSelectedAwayClub] = useState(null);
  const [selectedSportsHall, setSelectedSportsHall] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [selectedJudge1, setSelectedJudge1] = useState(null);
  const [selectedJudge2, setSelectedJudge2] = useState(null);
  const [selectedJudge3, setSelectedJudge3] = useState(null);
  const [selectedJudge4, setSelectedJudge4] = useState(null);
  const [selectedJudge5, setSelectedJudge5] = useState(null);
  const [selectedJudge6, setSelectedJudge6] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [date, setDate] = useState('');
  const [matchTime, setMatchTime] = useState('');
  const [isActive, setIsActive] = useState('0');
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const nextErrors = {
      competitions_id: !selectedCompetition,
      competition_round_id: !selectedRound,
      club_entities_id_1: !selectedHomeClub,
      club_entities_id_2: !selectedAwayClub,
      date: !date,
      match_time: !matchTime,
      sports_hall_id: !selectedSportsHall,
      judge_entities_id_1: !selectedJudge1,
      staff_entities_id: !selectedStaff,
      is_active: isActive === '' || isActive === null || isActive === undefined
    };

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const [
          competitionsResponse,
          sportsHallsResponse,
          supervisorsResponse,
          judgesResponse
        ] = await Promise.all([
          listCompetitions(),
          listSportsHall(),
          listSupervisors(),
          listJudges()
        ]);

        const competitionOptions = (competitionsResponse || []).map(item => ({
          value: item.competitions_id,
          label: item.name,
          raw: item
        }));
        setCompetitions(competitionOptions);

        const currentCompetition =
          competitionOptions.find(
            item => Number(item.value) === Number(initialData?.competitions_id)
          ) || null;
        setSelectedCompetition(currentCompetition);

        const hallOptions = (sportsHallsResponse || []).map(item => ({
          value: item.sports_hall_id,
          label: item.name || item.sports_hall_name
        }));
        setSportsHalls(hallOptions);
        setSelectedSportsHall(
          hallOptions.find(
            item => Number(item.value) === Number(initialData?.sports_hall_id)
          ) || null
        );

        const supervisorOptions = (supervisorsResponse || []).map(toPersonOption);
        setSupervisors(supervisorOptions);
        setSelectedSupervisor(
          supervisorOptions.find(
            item =>
              Number(item.value) === Number(initialData?.supervisor_entities_id)
          ) || null
        );

        const judgeOptions = (judgesResponse || []).map(toPersonOption);
        setJudges(judgeOptions);
        setSelectedJudge1(
          judgeOptions.find(
            item =>
              Number(item.value) === Number(initialData?.judge_entities_id_1)
          ) || null
        );
        setSelectedJudge2(
          judgeOptions.find(
            item =>
              Number(item.value) === Number(initialData?.judge_entities_id_2)
          ) || null
        );
        setSelectedJudge3(
          judgeOptions.find(
            item =>
              Number(item.value) === Number(initialData?.judge_entities_id_3)
          ) || null
        );
        setSelectedJudge4(
          judgeOptions.find(
            item =>
              Number(item.value) === Number(initialData?.judge_entities_id_4)
          ) || null
        );
        setSelectedJudge5(
          judgeOptions.find(
            item =>
              Number(item.value) === Number(initialData?.judge_entities_id_5)
          ) || null
        );
        setSelectedJudge6(
          judgeOptions.find(
            item =>
              Number(item.value) === Number(initialData?.judge_entities_id_6)
          ) || null
        );
        const staffOptions = judgeOptions;
        setStaff(staffOptions);
        setSelectedStaff(
          staffOptions.find(
            item => Number(item.value) === Number(initialData?.staff_entities_id)
          ) || null
        );

        setDate(
          initialData?.date
            ? format(parseISO(initialData.date), 'yyyy-MM-dd')
            : ''
        );
        setMatchTime(initialData?.match_time || '');
        setIsActive(
          initialData?.is_active === null || initialData?.is_active === undefined
            ? '0'
            : String(initialData.is_active)
        );
      } catch (error) {
        console.error('Greska prilikom ucitavanja sifarnika:', error);
      }
    };

    fetchStaticData();
  }, [initialData]);

  useEffect(() => {
    const fetchDependencies = async () => {
      if (!selectedCompetition?.raw) {
        setRounds([]);
        setClubs([]);
        setSelectedRound(null);
        setSelectedHomeClub(null);
        setSelectedAwayClub(null);
        return;
      }

      setLoadingDependencies(true);
      try {
        const [roundsResponse, clubsResponse] = await Promise.all([
          listCompetitionRounds({
            competitions_id: selectedCompetition.value
          }),
          listCompetitionClubs(selectedCompetition.raw.competitions_guid)
        ]);

        const roundOptions = (roundsResponse || []).map(item => ({
          value: item.competition_round_id,
          label: item.match_round_name,
          raw: item
        }));
        setRounds(roundOptions);
        setSelectedRound(
          roundOptions.find(
            item =>
              Number(item.value) === Number(initialData?.competition_round_id)
          ) || null
        );

        const clubOptions = (clubsResponse || [])
          .map(item => ({
            value: getClubEntityId(item),
            label: item.clubs_name || item.name,
            raw: item
          }))
          .filter(item => item.value !== null);
        setClubs(clubOptions);
        setSelectedHomeClub(
          clubOptions.find(
            item => Number(item.value) === Number(initialData?.club_entities_id_1)
          ) || null
        );
        setSelectedAwayClub(
          clubOptions.find(
            item => Number(item.value) === Number(initialData?.club_entities_id_2)
          ) || null
        );
      } catch (error) {
        console.error('Greska prilikom ucitavanja zavisnih podataka:', error);
        setRounds([]);
        setClubs([]);
      } finally {
        setLoadingDependencies(false);
      }
    };

    fetchDependencies();
  }, [initialData, selectedCompetition]);

  const availableAwayClubs = useMemo(() => {
    if (!selectedHomeClub) return clubs;
    return clubs.filter(
      item => Number(item.value) !== Number(selectedHomeClub.value)
    );
  }, [clubs, selectedHomeClub]);

  const handleSubmit = async event => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error('Sva polja su obavezna.');
      return;
    }

    if (Number(selectedHomeClub.value) === Number(selectedAwayClub.value)) {
      toast.error('Domaćin i gost ne mogu biti isti klub.');
      return;
    }

    setIsSubmitting(true);
    try {
      await editMatch(initialData.matches_guid, {
        competition_round_id: Number(selectedRound.value),
        club_entities_id_1: Number(selectedHomeClub.value),
        club_entities_id_2: Number(selectedAwayClub.value),
        date,
        match_time: matchTime,
        sports_hall_id: Number(selectedSportsHall.value),
        supervisor_entities_id: selectedSupervisor
          ? Number(selectedSupervisor.value)
          : null,
        judge_entities_id_1: Number(selectedJudge1.value),
        judge_entities_id_2: selectedJudge2 ? Number(selectedJudge2.value) : null,
        judge_entities_id_3: selectedJudge3 ? Number(selectedJudge3.value) : null,
        judge_entities_id_4: selectedJudge4 ? Number(selectedJudge4.value) : null,
        judge_entities_id_5: selectedJudge5 ? Number(selectedJudge5.value) : null,
        judge_entities_id_6: selectedJudge6 ? Number(selectedJudge6.value) : null,
        staff_entities_id: Number(selectedStaff.value),
        is_active: Number(isActive)
      });

      toast.success('Utakmica je uspješno izmijenjena.');
      onSave?.();
      onClose?.();
    } catch (error) {
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Došlo je do greške prilikom čuvanja.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <Card.Body className="bg-body-tertiary">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="competitions_id">
              <Form.Label>Takmičenja</Form.Label>
              <Select
                options={competitions}
                value={selectedCompetition}
                classNamePrefix="react-select"
                placeholder="Pretraži takmičenje..."
                onChange={option => setSelectedCompetition(option || null)}
                isDisabled
              />
              {errors.competitions_id && (
                <div className="text-danger mt-1">Polje je obavezno.</div>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="4" controlId="competition_round_id">
              <Form.Label>Kolo</Form.Label>
              <Select
                options={rounds}
                value={selectedRound}
                classNamePrefix="react-select"
                placeholder="Pretraži kolo..."
                onChange={option => setSelectedRound(option || null)}
                isDisabled={loadingDependencies || isSubmitting || !selectedCompetition || readOnly}
                isLoading={loadingDependencies}
              />
              {errors.competition_round_id && (
                <div className="text-danger mt-1">Polje je obavezno.</div>
              )}
            </Form.Group>
            <Form.Group as={Col} md="4" controlId="date">
              <Form.Label>Datum</Form.Label>
              <Form.Control
                type="date"
                value={date}
                onChange={event => setDate(event.target.value)}
                isInvalid={errors.date}
                disabled={isSubmitting || readOnly}
              />
              <Form.Control.Feedback type="invalid">
                Polje je obavezno.
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="4" controlId="match_time">
              <Form.Label>Vrijeme</Form.Label>
              <Form.Control
                type="time"
                step="1"
                value={matchTime}
                onChange={event => setMatchTime(event.target.value)}
                isInvalid={errors.match_time}
                disabled={isSubmitting || readOnly}
              />
              <Form.Control.Feedback type="invalid">
                Polje je obavezno.
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="club_entities_id_1">
              <Form.Label>Domaćin</Form.Label>
              <Select
                options={clubs}
                value={selectedHomeClub}
                classNamePrefix="react-select"
                placeholder="Pretraži domaćina..."
                onChange={option => {
                  setSelectedHomeClub(option || null);
                  if (
                    selectedAwayClub &&
                    Number(selectedAwayClub.value) === Number(option?.value)
                  ) {
                    setSelectedAwayClub(null);
                  }
                }}
                isDisabled={loadingDependencies || isSubmitting || !selectedCompetition || readOnly}
                isLoading={loadingDependencies}
              />
              {errors.club_entities_id_1 && (
                <div className="text-danger mt-1">Polje je obavezno.</div>
              )}
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="club_entities_id_2">
              <Form.Label>Gost</Form.Label>
              <Select
                options={availableAwayClubs}
                value={selectedAwayClub}
                classNamePrefix="react-select"
                placeholder="Pretraži gosta..."
                onChange={option => setSelectedAwayClub(option || null)}
                isDisabled={loadingDependencies || isSubmitting || !selectedCompetition || readOnly}
                isLoading={loadingDependencies}
              />
              {errors.club_entities_id_2 && (
                <div className="text-danger mt-1">Polje je obavezno.</div>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="sports_hall_id">
              <Form.Label>Dvorana</Form.Label>
              <Select
                options={sportsHalls}
                value={selectedSportsHall}
                classNamePrefix="react-select"
                placeholder="Pretraži dvoranu..."
                onChange={option => setSelectedSportsHall(option || null)}
                isClearable
                isDisabled={isSubmitting || readOnly}
              />
              {errors.sports_hall_id && (
                <div className="text-danger mt-1">Polje je obavezno.</div>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="judge_entities_id_1">
              <Form.Label>Sudija 1</Form.Label>
              <Select
                options={judges}
                value={selectedJudge1}
                classNamePrefix="react-select"
                placeholder="Pretraži sudiju..."
                onChange={option => setSelectedJudge1(option || null)}
                isClearable
                isDisabled={isSubmitting || readOnly}
              />
              {errors.judge_entities_id_1 && (
                <div className="text-danger mt-1">Polje je obavezno.</div>
              )}
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="judge_entities_id_2">
              <Form.Label>Sudija 2</Form.Label>
              <Select
                options={judges}
                value={selectedJudge2}
                classNamePrefix="react-select"
                placeholder="Pretraži sudiju..."
                onChange={option => setSelectedJudge2(option || null)}
                isClearable
                isDisabled={isSubmitting || readOnly}
              />
            </Form.Group>

          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="judge_entities_id_3">
              <Form.Label>Linijski sudija 1</Form.Label>
              <Select
                  options={judges}
                  value={selectedJudge3}
                  classNamePrefix="react-select"
                  placeholder="Pretraži sudiju..."
                  onChange={option => setSelectedJudge3(option || null)}
                  isClearable
                  isDisabled={isSubmitting || readOnly}
              />
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="judge_entities_id_4">
              <Form.Label>Linijski sudija 2</Form.Label>
              <Select
                options={judges}
                value={selectedJudge4}
                classNamePrefix="react-select"
                placeholder="Pretraži sudiju..."
                onChange={option => setSelectedJudge4(option || null)}
                isClearable
                isDisabled={isSubmitting || readOnly}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="judge_entities_id_5">
              <Form.Label>Čelendž sudija</Form.Label>
              <Select
                options={judges}
                value={selectedJudge5}
                classNamePrefix="react-select"
                placeholder="Pretraži sudiju..."
                onChange={option => setSelectedJudge5(option || null)}
                isClearable
                isDisabled={isSubmitting || readOnly}
              />
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="judge_entities_id_6">
              <Form.Label>Čelendž operater</Form.Label>
              <Select
                options={judges}
                value={selectedJudge6}
                classNamePrefix="react-select"
                placeholder="Pretraži sudiju..."
                onChange={option => setSelectedJudge6(option || null)}
                isClearable
                isDisabled={isSubmitting || readOnly}
              />
            </Form.Group>
          </Row>

          <Row className="mb-4">
            <Form.Group as={Col} md="4" controlId="supervisor_entities_id">
              <Form.Label>Supervizor</Form.Label>
              <Select
                options={supervisors}
                value={selectedSupervisor}
                classNamePrefix="react-select"
                placeholder="Pretraži supervizora..."
                onChange={option => setSelectedSupervisor(option || null)}
                isClearable
                isDisabled={isSubmitting || readOnly}
              />
            </Form.Group>
            <Form.Group as={Col} md="4" controlId="staff_entities_id">
              <Form.Label>Zapisničar</Form.Label>
              <Select
                options={staff}
                value={selectedStaff}
                classNamePrefix="react-select"
                placeholder="Pretraži zapisničara..."
                onChange={option => setSelectedStaff(option || null)}
                isSearchable
                isClearable
                isDisabled={isSubmitting}
              />
              {errors.staff_entities_id && (
                <div className="text-danger mt-1">Polje je obavezno.</div>
              )}
            </Form.Group>
            <Form.Group as={Col} md="4" controlId="is_active">
              <Form.Label>Aktivan</Form.Label>
              <Form.Select
                value={isActive}
                onChange={event => setIsActive(event.target.value)}
                isInvalid={errors.is_active}
                disabled={isSubmitting || readOnly}
              >
                <option value="1">Da</option>
                <option value="0">Ne</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Polje je obavezno.
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Zatvori
            </Button>
            {!readOnly && (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Sačuvaj...
                  </>
                ) : (
                  'Sačuvaj'
                )}
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

EditMatch.propTypes = {
  initialData: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  readOnly: PropTypes.bool
};

export default EditMatch;
